
import { useQuery } from '@tanstack/react-query';
import { 
  STYLE_FILTERS, 
  INFLUENCE_FILTERS, 
  COLOR_FILTERS, 
  BACKGROUND_FILTERS, 
  ENVIRONMENT_FILTERS, 
  MOOD_FILTERS 
} from '../data';

// Combine all filters into a single lookup map
const ALL_FILTERS = [
  ...STYLE_FILTERS,
  ...INFLUENCE_FILTERS,
  ...COLOR_FILTERS,
  ...BACKGROUND_FILTERS,
  ...ENVIRONMENT_FILTERS,
  ...MOOD_FILTERS
];

const FILTER_NAME_MAP = ALL_FILTERS.reduce((acc, filter) => {
  acc[filter.id] = filter.prompt_name;
  return acc;
}, {} as Record<string, string>);

/**
 * Hook to fetch a single filter name by ID from static data
 */
export const useStaticFilterName = (id: string | undefined) => {
  return useQuery({
    queryKey: ['static-filter-name', id],
    enabled: !!id,
    queryFn: async () => {
      if (!id) return null;
      
      // Simulate async behavior to maintain interface compatibility
      await new Promise(resolve => setTimeout(resolve, 1));
      
      return FILTER_NAME_MAP[id] || null;
    },
    staleTime: Infinity, // Static data never goes stale
    gcTime: Infinity, // Keep in cache forever
  });
};

/**
 * Hook to fetch multiple filter names by IDs from static data
 */
export const useStaticFilterNames = (ids: string[] | undefined) => {
  return useQuery({
    queryKey: ['static-filter-names', ids ? ids.join(',') : 'none'],
    enabled: !!ids && ids.length > 0,
    queryFn: async () => {
      if (!ids || ids.length === 0) return {};
      
      // Simulate async behavior to maintain interface compatibility
      await new Promise(resolve => setTimeout(resolve, 1));
      
      const nameMap: Record<string, string> = {};
      ids.forEach(id => {
        if (FILTER_NAME_MAP[id]) {
          nameMap[id] = FILTER_NAME_MAP[id];
        }
      });
      
      return nameMap;
    },
    staleTime: Infinity, // Static data never goes stale
    gcTime: Infinity, // Keep in cache forever
  });
};
